import React from 'react';
import useWebSocket from 'react-use-websocket';
import type { GreenCheckWebsocketAction, GreenCheckWebSocketMessage } from '@gcv/shared';

import { environment } from '../environments/environment';
import { getNotificationStore } from '../stores/NotificationStore';

export type WebSocketListener = (message: GreenCheckWebSocketMessage) => void;
export type WebSocketListenerMap = Partial<Record<GreenCheckWebsocketAction, WebSocketListener>>;

/**
 * Consolidated implementation of websockets
 *
 * Mount this once per app, high in the tree, and declare what happens for each message action.
 * The listener for an action runs once per message received - there is no de-duplication, so
 * repeated messages with the same action are each handled.
 *
 * Only one component may call this hook: the connection is shared, but each caller gets its own
 * message callback, so a second caller would handle every message a second time.
 *
 * ref: https://www.npmjs.com/package/react-use-websocket
 *
 * @param userId used as the connection token
 * @param listeners map of websocket action -> action to perform. May be declared inline;
 *                  it is read through a ref so a re-render never misses a message.
 * @param connect set to false to hold off connecting, e.g. while the user is still loading
 */
export const useGcvWebSocket = (userId: string, listeners: WebSocketListenerMap = {}, connect = true) => {
  const notificationStore = getNotificationStore();

  const listenersRef = React.useRef(listeners);
  listenersRef.current = listeners;

  const { sendMessage, sendJsonMessage, lastMessage, lastJsonMessage, readyState, getWebSocket } =
    useWebSocket(
      `${environment.gcvConfig.webSocketUrl}`,
      {
        queryParams: { token: userId },
        //Will attempt to reconnect on all close events, such as server shutting down
        shouldReconnect: () => true,
        // If set to true, a new WebSocket will not be instantiated if one for the same url has already
        // been created for another component. Once all subscribing components have either unmounted or
        // changed their target socket url, shared WebSockets will be closed and cleaned up.
        // No other APIs should be affected by this.
        share: true,
        onMessage: (event) => {
          // parse the message
          const socketMessage = JSON.parse(event.data) as GreenCheckWebSocketMessage;
          // save it to the store for other pages to react to
          notificationStore.handleWebsocket(socketMessage);

          // run the listener declared for this action
          try {
            listenersRef.current[socketMessage.action]?.(socketMessage);
          } catch (error) {
            console.error(`Websocket listener failed for action "${socketMessage.action}"`, error);
          }
        }
      },
      connect && Boolean(userId)
    );

  return { sendMessage, sendJsonMessage, lastMessage, lastJsonMessage, readyState, getWebSocket };
};
